import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, Vote, MonitorCog, Smartphone, ArrowRight, Lock, FileCheck2 } from "lucide-react";

const surfaces = [
  {
    to: "/polling",
    title: "Polling Device",
    tag: "Kiosk · Offline-first",
    testid: "surface-polling",
    icon: Vote,
    accent: "#0A369D",
    desc: "Voter authentication with mock retina + Aadhaar adapter, accessibility fallback, EVM ballot and VVPAT slip window. Ballot casting works with no network.",
  },
  {
    to: "/admin",
    title: "ECI Admin Portal",
    tag: "Operations · Forensic",
    testid: "surface-admin",
    icon: MonitorCog,
    accent: "#138808",
    desc: "Booth registry with device certificates, hash-chain sync viewer, live tallies, incident review, VVPAT reconciliation, and an adversarial test panel.",
  },
  {
    to: "/operator",
    title: "Operator Companion",
    tag: "Field · Mobile",
    testid: "surface-operator",
    icon: Smartphone,
    accent: "#FF9933",
    desc: "Presiding-officer setup wizard, device diagnostics, and one-tap incident reporting for the polling station.",
  },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-white text-zinc-900">
      {/* Top strip: tricolor + serial */}
      <div className="w-full h-1 tricolor-strip" />
      <header className="border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 border border-zinc-900 flex items-center justify-center">
              <Lock className="w-4 h-4" strokeWidth={2.5} />
            </div>
            <div>
              <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">
                Election Commission · Prototype Console
              </div>
              <div className="font-display font-black text-lg leading-none">
                BHARATVARSHA EVM/VVPAT
              </div>
            </div>
          </div>
          <div className="hidden md:flex items-center gap-6 text-xs font-mono text-zinc-600">
            <span>BUILD: PATENT-DRAFT · R1</span>
            <span className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 bg-[#138808] rounded-full inline-block" />
              MOCK-ONLY · NO REAL BIOMETRICS
            </span>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative border-b border-zinc-200 overflow-hidden">
        <div className="absolute inset-0 tech-grid opacity-40 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 py-16 md:py-24 relative">
          <div className="max-w-3xl">
            <div className="text-xs font-mono uppercase tracking-[0.25em] text-[#0A369D] mb-6">
              [ PATENT-ORIENTED DESIGN PROTOTYPE · NOT FOR DEPLOYMENT ]
            </div>
            <h1 className="font-display font-black uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight">
              Offline-first,<br />
              biometric-assisted<br />
              <span className="text-[#0A369D]">EVM &amp; VVPAT</span> stack.
            </h1>
            <p className="mt-8 text-lg text-zinc-600 max-w-2xl leading-relaxed">
              Three coordinated surfaces &mdash; a sealed polling device, an ECI operations
              portal, and a field companion &mdash; connected by a hash-chained event log,
              a strictly separated identity/ballot boundary, and a paper trail that
              always wins a dispute.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link
                to="/polling"
                data-testid="hero-open-polling"
                className="group inline-flex items-center gap-2 bg-zinc-900 text-white px-5 py-3 hover:bg-zinc-800 transition-colors"
              >
                Open Polling Kiosk
                <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </Link>
              <Link
                to="/admin"
                data-testid="hero-open-admin"
                className="inline-flex items-center gap-2 border border-zinc-900 px-5 py-3 hover:bg-zinc-100 transition-colors"
              >
                Enter Admin Portal
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Surfaces */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid md:grid-cols-3 gap-6">
          {surfaces.map((s, i) => (
            <motion.div
              key={s.to}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.08 }}
            >
              <Link
                to={s.to}
                data-testid={s.testid}
                className="block border border-zinc-200 hover:border-zinc-900 p-6 h-full transition-colors bg-white"
              >
                <div className="flex items-start justify-between mb-6">
                  <div
                    className="w-11 h-11 flex items-center justify-center border"
                    style={{ borderColor: s.accent, color: s.accent }}
                  >
                    <s.icon className="w-5 h-5" strokeWidth={2} />
                  </div>
                  <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                    {s.tag}
                  </span>
                </div>
                <h3 className="font-display font-bold text-2xl mb-2">{s.title}</h3>
                <p className="text-sm text-zinc-600 leading-relaxed">{s.desc}</p>
                <div className="mt-6 flex items-center gap-2 text-sm font-medium">
                  Launch <ArrowRight className="w-4 h-4" />
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Principles */}
      <section className="border-t border-zinc-200 bg-zinc-50">
        <div className="max-w-7xl mx-auto px-6 py-14 grid md:grid-cols-4 gap-8">
          {[
            { icon: ShieldCheck, t: "Identity ≠ Ballot", d: "Retina/Aadhaar result yields only an eligibility token. Vote content is stored on a separate chain." },
            { icon: FileCheck2, t: "Paper Wins Disputes", d: "Every accepted vote prints a VVPAT slip; discrepancies freeze evidence and open an audit case." },
            { icon: Lock, t: "Hash-chained Logs", d: "Append-only device log; sync verifies the chain and rejects duplicates or out-of-order events." },
            { icon: Vote, t: "No Silent Rejection", d: "Biometric failures route to a legally authorised, dual-officer fallback — never to disenfranchisement." },
          ].map((p) => (
            <div key={p.t} className="flex flex-col gap-3">
              <p.icon className="w-5 h-5 text-[#0A369D]" strokeWidth={2} />
              <div className="font-display font-bold text-base">{p.t}</div>
              <div className="text-sm text-zinc-600 leading-relaxed">{p.d}</div>
            </div>
          ))}
        </div>
      </section>

      <footer className="border-t border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 py-6 flex items-center justify-between text-xs font-mono text-zinc-500">
          <span>© Prototype · Research use only · Aadhaar/retina calls are mocked.</span>
          <span>v0.1.0-patent-draft</span>
        </div>
      </footer>
    </div>
  );
}
